import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { settingsPatch } from "@shared/schemas/settings";
import { validateCompanySettings, validateOrganization } from "@shared/validation/admin";
import { evaluation, parseInput, type ValidationIssue } from "@shared/validation/issues";
import { ApiError, errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery } from "@web/lib/validation";
import { api, companyQuery, type Company } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { Button, Inline, MoneyField, Page, Picker, Skeleton, TextArea, TextField, Toggle, Workspace, FormSection, FormGrid, Region, SaveBar, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";
import { AdminsOnly } from "./Settings";

const TIMEZONES = ["America/Chicago", "America/New_York", "America/Denver", "America/Phoenix", "America/Los_Angeles", "America/Anchorage", "Pacific/Honolulu"];
const list = (v: unknown[]) => v.map(String).join(", ");
const unlist = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

function initial(c: Company) {
  const o = c.organization;
  const s = c.settings;
  return {
    name: o.name,
    legalName: o.legalName ?? "",
    dba: o.dba ?? "",
    address: o.address ?? "",
    phone: o.phone ?? "",
    email: o.email ?? "",
    timezone: o.timezone,
    depositPct: s.depositPct,
    balanceDaysBefore: String(s.balanceDaysBefore),
    cardFeePct: s.cardFeePct,
    holdAmount: s.holdAmount as number | null,
    holdHours: String(s.holdHours),
    damageDefaultOn: Boolean(s.damageDefaultOn),
    agentName: s.agentName ?? "",
    agentPhone: s.agentPhone ?? "",
    emailFrom: s.emailFrom ?? "",
    emailFromName: s.emailFromName ?? "",
    replyTo: s.replyTo ?? "",
    notifyEmails: list(s.notifyEmails),
    notifyPhones: list(s.notifyPhones),
  };
}
type Form = ReturnType<typeof initial>;

/** Settings › Company (SPEC §3.1–3.2 basics): organization fields + deposit / fee / hold / damage / comms defaults. */
export function SettingsCompany() {
  const me = useMe();
  if (!me.companyAdmin) return <AdminsOnly />;
  return <CompanyForm />;
}

function CompanyForm() {
  const q = useQuery(companyQuery);
  if (!q.data)
    return (
      <Page>
        <Skeleton variant="cards" count={3} />
      </Page>
    );
  return <CompanyEditor key={q.data.organization.id} company={q.data} />;
}

function CompanyEditor({ company }: { company: Company }) {
  const toast = useToast();
  const [baseline, setBaseline] = useState(company);
  const [form, setForm] = useState<Form>(() => initial(company));
  const saved = useQuery(recordValidationQuery("settings", company.organization.id));
  const dirty = JSON.stringify(form) !== JSON.stringify(initial(baseline));
  const nul = (s: string) => s.trim() || null;
  const toBody = (form: Form) => ({
      organization: { name: form!.name, legalName: nul(form!.legalName), dba: nul(form!.dba), address: nul(form!.address), phone: nul(form!.phone), email: nul(form!.email), timezone: form!.timezone },
      settings: {
        depositPct: form!.depositPct,
        balanceDaysBefore: Number(form!.balanceDaysBefore),
        cardFeePct: form!.cardFeePct,
        holdAmount: form!.holdAmount,
        holdHours: Number(form!.holdHours),
        damageDefaultOn: form!.damageDefaultOn,
        agentName: nul(form!.agentName),
        agentPhone: nul(form!.agentPhone),
        emailFrom: nul(form!.emailFrom),
        emailFromName: nul(form!.emailFromName),
        replyTo: nul(form!.replyTo),
        notifyEmails: unlist(form!.notifyEmails),
        notifyPhones: unlist(form!.notifyPhones),
      },
    });
  const current = toBody(form), previous = toBody(initial(baseline));
  const body = { organization: changedValues(current.organization, previous.organization), settings: changedValues(current.settings, previous.settings) };
  const flat = (issue: ValidationIssue): ValidationIssue => ({ ...issue, fields: issue.fields.map(path => path.replace(/^(organization|settings)\./, "")) });
  const parsed = parseInput(settingsPatch, body, { type: "settings", id: company.organization.id }, "settings.save");
  const local = evaluation([...parsed.issues.map(flat), ...validateOrganization({ ...baseline.organization, ...body.organization }, { before: baseline.organization, changedFields: Object.keys(body.organization) }).issues, ...validateCompanySettings({ ...baseline.settings, ...body.settings }, { before: baseline.settings, changedFields: Object.keys(body.settings) }).issues], false);
  const validation = useFormValidation(local);
  const { busy, field } = validation;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => validation.edit(() => setForm((f) => ({ ...f, [k]: v })));
  async function save() {
    if (!parsed.success || !validation.begin("settings.save")) return;
    try {
      const c = await api.patchCompany(parsed.data);
      setBaseline(c);
      setForm(initial(c));
      validation.clear();
      void saved.refetch();
      toast.success("Company saved");
    } catch (e) {
      validation.reject(e);
      if (e instanceof ApiError && e.issues) validation.report(e.issues.map(flat));
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <Page>
      <UnsavedChanges dirty={dirty} />
      <Workspace variant="form">
        <Region area="header"><ValidationSummary title={saved.data?.checksComplete && !saved.isError ? `Saved record checks · ${saved.data.issues.length ? `${saved.data.issues.length} to review` : "no findings"}` : undefined} result={saved.data ? { ...saved.data, issues: saved.data.issues.map(flat) } : undefined} loading={saved.isFetching} unavailable={saved.isError} onRetry={() => void saved.refetch()} saved /><ValidationSummary result={validation.result} local unavailable={validation.unavailable} /></Region>
        <FormSection title="Company">
          <TextField label="Name" value={form.name} onChange={(e) => set("name", e.target.value)} {...field("name")} />
          <TextField label="Legal name" value={form.legalName} onChange={(e) => set("legalName", e.target.value)} {...field("legalName")} />
          <TextField label="DBA" value={form.dba} onChange={(e) => set("dba", e.target.value)} {...field("dba")} />
          <TextArea label="Address" value={form.address} onChange={(e) => set("address", e.target.value)} {...field("address")} />
          <Inline gap={2} align="start">
            <TextField label="Phone" type="tel" className="flex-1" value={form.phone} onChange={(e) => set("phone", e.target.value)} {...field("phone")} />
            <TextField label="Email" type="email" className="flex-1" value={form.email} onChange={(e) => set("email", e.target.value)} {...field("email")} />
          </Inline>
          <Picker label="Timezone" value={form.timezone} onChange={(v) => set("timezone", v)} options={[...new Set([form.timezone, ...TIMEZONES])].map((z) => ({ value: z, label: z }))} {...field("timezone")} />
        </FormSection>
        <FormSection title="Money defaults">
          <Inline gap={2} align="start">
            <TextField label="Deposit %" type="number" inputMode="decimal" min={0} max={100} className="flex-1" value={form.depositPct} onChange={(e) => set("depositPct", e.target.value)} {...field("depositPct")} />
            <TextField label="Balance due (days)" hint="Before the event" type="number" inputMode="numeric" min={0} className="flex-1" value={form.balanceDaysBefore} onChange={(e) => set("balanceDaysBefore", e.target.value)} {...field("balanceDaysBefore")} />
          </Inline>
          <TextField label="Processing fee %" type="number" inputMode="decimal" min={0} step="0.1" value={form.cardFeePct} onChange={(e) => set("cardFeePct", e.target.value)} hint="Added as a line on every new booking; the booker can remove or change it." {...field("cardFeePct")} />
          <Inline gap={2} align="start">
            <MoneyField label="Hold amount" className="flex-1" value={form.holdAmount} onChange={(v) => set("holdAmount", v)} {...field("holdAmount")} />
            <TextField label="Hold hours" type="number" inputMode="numeric" min={1} className="flex-1" value={form.holdHours} onChange={(e) => set("holdHours", e.target.value)} {...field("holdHours")} />
          </Inline>
          <Toggle label="Damage deposit on by default" description="Amount comes from each venue" checked={form.damageDefaultOn} onChange={(v) => set("damageDefaultOn", v)} disabled={busy} />
        </FormSection>
        <Region area="header"><FormSection title="Messages">
          <FormGrid>
          <Inline gap={2} align="start">
            <TextField label="Agent name" className="flex-1" value={form.agentName} onChange={(e) => set("agentName", e.target.value)} hint="Signs texts and emails" {...field("agentName")} />
            <TextField label="Agent phone" type="tel" className="flex-1" value={form.agentPhone} onChange={(e) => set("agentPhone", e.target.value)} {...field("agentPhone")} />
          </Inline>
          <TextField label="Email from" type="email" value={form.emailFrom} onChange={(e) => set("emailFrom", e.target.value)} {...field("emailFrom")} />
          <TextField label="Email from name" value={form.emailFromName} onChange={(e) => set("emailFromName", e.target.value)} {...field("emailFromName")} />
          <TextField label="Reply-to" type="email" value={form.replyTo} onChange={(e) => set("replyTo", e.target.value)} {...field("replyTo")} />
          <TextField label="Notify emails" value={form.notifyEmails} onChange={(e) => set("notifyEmails", e.target.value)} hint="Comma-separated; staff alerts go here" {...field("notifyEmails")} />
          <TextField label="Notify phones" value={form.notifyPhones} onChange={(e) => set("notifyPhones", e.target.value)} hint="Comma-separated" {...field("notifyPhones")} />
          </FormGrid>
        </FormSection></Region>
        <Region area="header"><SaveBar>
        <Button loading={busy} disabled={!dirty} onClick={save}>
          Save company
        </Button>
        </SaveBar></Region>
      </Workspace>
    </Page>
  );
}
